import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ReviewService from "../../services/ReviewService";
import { Review } from "../../../lib/types/review";
import { useGlobals } from "../../hooks/useGlobal";
import { Messages, serverApi } from "../../../lib/config";
import { sweetErrorHandling, sweetTopSuccessAlert } from "../../../lib/sweetAlert";
import "../../../css/community.css";

export default function CommunityPage() {
  const { authMember } = useGlobals();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});

  const load = async () => {
    const service = new ReviewService();
    const data = await service.getCommunity(1, 20);
    setReviews(data.items || []);
  };

  useEffect(() => {
    load().catch((err) => console.log(err));
  }, []);

  const requireAuth = () => {
    if (!authMember) {
      sweetErrorHandling(Messages.error2);
      return false;
    }
    return true;
  };

  const onLike = async (id: string) => {
    if (!requireAuth()) return;
    try {
      const service = new ReviewService();
      await service.likeReview(id);
      await load();
    } catch (err) {
      sweetErrorHandling(err);
    }
  };

  const onReply = async (id: string) => {
    if (!requireAuth()) return;
    try {
      const text = (replyDrafts[id] || "").trim();
      if (!text) throw new Error(Messages.error4);
      const service = new ReviewService();
      await service.replyReview(id, text);
      setReplyDrafts((prev) => ({ ...prev, [id]: "" }));
      await sweetTopSuccessAlert("Reply posted", 900);
      await load();
    } catch (err) {
      sweetErrorHandling(err);
    }
  };

  return (
    <div className="community-page">
      <Container>
        <Typography className="community-title" variant="h3">
          Community
        </Typography>
        <Typography className="community-subtitle">
          Reader reviews from active BooClub members
        </Typography>
        <Stack spacing={3} sx={{ mt: 4, pb: 8 }}>
          {reviews.length === 0 && (
            <Box className="no-data">No reviews yet. Be the first!</Box>
          )}
          {reviews.map((review) => {
            const product: any = review.productId;
            const member: any = review.memberId;
            const cover = product?.productImages?.[0]
              ? `${serverApi}/${product.productImages[0]}`
              : "/icons/booclub.svg";
            return (
              <Box className="review-card" key={review._id}>
                <Stack direction="row" spacing={2}>
                  <img className="review-cover" src={cover} alt="" />
                  <Box sx={{ flex: 1 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <strong>{member?.memberNick || "Reader"}</strong>
                        <div className="muted">
                          {product?.productName || "Book"} ·{" "}
                          {review.createdAt
                            ? new Date(review.createdAt).toLocaleDateString()
                            : ""}
                        </div>
                      </Box>
                      <Rating value={review.reviewRating} readOnly size="small" />
                    </Stack>
                    <p>{review.reviewText}</p>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        size="small"
                        startIcon={<FavoriteBorderIcon />}
                        onClick={() => onLike(review._id)}
                      >
                        {review.reviewLikes || 0}
                      </Button>
                    </Stack>
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {(review.replies || []).map((reply) => (
                        <Box className="reply-item" key={reply._id}>
                          <strong>
                            {(reply.memberId as any)?.memberNick || "Member"}
                          </strong>
                          <span>{reply.replyText}</span>
                        </Box>
                      ))}
                    </Stack>
                    {authMember ? (
                      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Write a reply"
                          value={replyDrafts[review._id] || ""}
                          onChange={(e) =>
                            setReplyDrafts((prev) => ({
                              ...prev,
                              [review._id]: e.target.value,
                            }))
                          }
                        />
                        <Button
                          variant="contained"
                          onClick={() => onReply(review._id)}
                        >
                          Reply
                        </Button>
                      </Stack>
                    ) : null}
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Container>
    </div>
  );
}
